import React, { memo, useCallback, useContext, useMemo } from 'react'

import {
  Icon,
  ExternalLink,
  ArrowExternal,
  Download2,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import Modal from 'Pods/Shared/Modal/Modal'
import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import SoftwareIcon from 'Pods/ThreeDStudio/Shared/SoftwareIcon/SoftwareIcon'
import { getDccPluginDownloadUrl, getPluginKey } from 'Pods/ThreeDStudio/Shared/Utils/dccPluginKeys'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import {
  closeSendToModal,
  sendModelToDcc,
} from 'Pods/ThreeDStudio/Store/actions/dccBridge'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

const downloadIcon = <Icon svgComponent={Download2} color="primary" size={iconSize} notSpaced />
const sendArrowIcon = <Icon svgComponent={ArrowExternal} color="primary" size={iconSize} notSpaced />
const guideIcon = <Icon svgComponent={ExternalLink} color="primary" size={iconSize} notSpaced />

function SendToModal() {
  const t = useTranslation()
  const isOpen = useThreeDStudioStore(state => state.dccBridge.isSendModalOpen)
  const selectedName = useThreeDStudioStore(state => state.dccBridge.selectedSoftwareName)
  const { dccSoftwares: softwares } = useContext(ThreeDStudioPageContext)

  const software = useMemo(
    () => softwares.find(s => s.name === selectedName) || null,
    [softwares, selectedName]
  )

  const handleSend = useCallback(() => {
    if (!software) return
    sendModelToDcc(software.name)
  }, [software])

  if (!isOpen || !software) return null

  // Same installer link the DCC popover's "Download Plugin" uses (pinned to prod).
  const downloadUrl = getDccPluginDownloadUrl(getPluginKey(software.name))
  const sendToLabel = t('footerActions.sendTo.labelWithName', { name: software.name })

  const headerContent = (
    <div className="dcc-send-modal__header">
      <SoftwareIcon iconName={software.iconName} />
      <span className="dcc-send-modal__title cgt-heading-100">{sendToLabel}</span>
    </div>
  )

  const footerContent = (
    <>
      <span className="dcc-send-modal__footer-text cgt-body-base">{t('footerActions.sendTo.needHelp')}</span>
      <a
        className="dcc-send-modal__footer-link cgt-link-primary-accented"
        href={software.guideLink}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span>{t('footerActions.sendTo.setupGuide')}</span>
        {guideIcon}
      </a>
    </>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeSendToModal}
      width="small"
      header={headerContent}
      footer={footerContent}
      canClose
      customClass="dcc-send-modal"
      ariaLabel={`Send to ${software.name}`}
    >
      <ol className="dcc-send-modal__steps">
        <li className="dcc-send-modal__step">
          <span className="dcc-send-modal__step-number cgt-heading-50">1.</span>
          <div className="dcc-send-modal__step-body">
            <p className="dcc-send-modal__step-text cgt-heading-50">{t('footerActions.sendTo.step1')}</p>
            <a
              className="dcc-send-modal__step-link cgt-link-primary"
              href={downloadUrl}
            >
              {downloadIcon}
              <span>{t('footerActions.sendTo.downloadPlugin')}</span>
            </a>
          </div>
        </li>
        <li className="dcc-send-modal__step">
          <span className="dcc-send-modal__step-number cgt-heading-50">2.</span>
          <div className="dcc-send-modal__step-body">
            <p className="dcc-send-modal__step-text cgt-heading-50">
              {t('footerActions.sendTo.step2')}
            </p>
          </div>
        </li>
        <li className="dcc-send-modal__step">
          <span className="dcc-send-modal__step-number cgt-heading-50">3.</span>
          <div className="dcc-send-modal__step-body">
            <p className="dcc-send-modal__step-text cgt-heading-50">{t('footerActions.sendTo.step3')}</p>
            <button
              type="button"
              className="dcc-send-modal__step-link cgt-link-primary"
              onClick={handleSend}
            >
              <span>{sendToLabel}</span>
              {sendArrowIcon}
            </button>
          </div>
        </li>
      </ol>
    </Modal>
  )
}

export default memo(SendToModal)
